<template>
  <section v-if="hasMapOrLink" class="rms-public-map rms-public-map--stack-below-header w-full" aria-label="Location" data-testid="public-map-section">
    <!-- Map: stored coordinates or coordinates parsed from saved map URL -->
    <div
      v-if="hasMapCoordinates"
      ref="mapEl"
      class="rms-public-map__container w-full min-h-[280px] rounded-lg overflow-hidden touch-manipulation"
      :class="containerClass"
      role="application"
      :aria-label="$t('public.mapAriaLabel')"
    />
    <!-- Fallback: no Leaflet coords — link to saved map URL (API may fill lat/lng later via geocoding + DB persist). -->
    <div
      v-if="savedMapUrlFallback && !hasMapCoordinates"
      class="rms-public-map__fallback flex min-h-[260px] w-full flex-col items-stretch justify-center gap-4 rounded-lg border border-slate-200/80 bg-slate-100/90 px-4 py-8 dark:border-zinc-700 dark:bg-zinc-800/90 sm:min-h-[280px]"
    >
      <p
        v-if="showOwnerMapHint"
        class="max-w-md mx-auto text-center text-sm text-slate-600 dark:text-slate-400"
      >
        {{ $t('public.mapOpenExternallyHint') }}
      </p>
      <a
        :href="savedMapUrlFallback"
        target="_blank"
        rel="noopener noreferrer"
        class="rms-public-map__link inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 self-center"
        :class="linkClass"
      >
        <span class="material-symbols-outlined text-lg" aria-hidden="true">map</span>
        {{ $t('public.openInMaps') }}
      </a>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { getRestaurantMapCoordinates, openStreetMapUrl } from '@/utils/googleMapsUrl'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix default marker icon with Vite (asset paths get hashed)
import markerIconUrl from 'leaflet/dist/images/marker-icon.png'
import markerIconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png'
const DefaultIcon = L.icon({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIconRetinaUrl,
  shadowUrl: markerShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

const props = defineProps({
  /** Restaurant object: address, latitude, longitude, google_maps_url (from PublicRestaurant or API) */
  restaurant: { type: Object, default: () => ({}) },
  /** Optional CSS class for the map container (e.g. template-specific height) */
  containerClass: { type: String, default: '' },
  /** Optional CSS class for the fallback external map link */
  linkClass: { type: String, default: 'bg-primary text-white hover:opacity-90 focus:ring-primary' },
  /** When true (owner viewing site), show setup hint for unparsed map links; guests only see "Open in maps". */
  showOwnerMapHint: { type: Boolean, default: false },
})

const { t } = useI18n()
const mapEl = ref(null)
let map = null
let marker = null
let detachMapResizeListeners = () => {}

function invalidateMapSize() {
  if (!map) return
  try {
    map.invalidateSize({ animate: false })
  } catch (_) {
    // ignore
  }
}

function attachMapResizeListeners(mapInstance) {
  let timeoutId = null
  const schedule = () => {
    if (timeoutId != null) clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => {
      timeoutId = null
      invalidateMapSize()
    }, 120)
  }
  window.addEventListener('resize', schedule)
  window.addEventListener('orientationchange', schedule)
  if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
    window.visualViewport.addEventListener('resize', schedule)
  }
  return () => {
    if (timeoutId != null) clearTimeout(timeoutId)
    window.removeEventListener('resize', schedule)
    window.removeEventListener('orientationchange', schedule)
    if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', schedule)
    }
  }
}

/** iOS Safari often reports 0×0 or stale size until after paint; re-measure a few times. */
function scheduleMapSizeSync() {
  if (!map) return
  invalidateMapSize()
  requestAnimationFrame(() => {
    invalidateMapSize()
    requestAnimationFrame(invalidateMapSize)
  })
  window.setTimeout(invalidateMapSize, 200)
  window.setTimeout(invalidateMapSize, 500)
}

function escapeHtml(text) {
  if (text == null || text === '') return ''
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/** Lat/lng for Leaflet: from restaurant fields or parsed from saved Google Maps URL */
const mapCoords = computed(() => getRestaurantMapCoordinates(props.restaurant))

const lat = computed(() => mapCoords.value?.lat ?? null)
const lng = computed(() => mapCoords.value?.lng ?? null)

const hasMapCoordinates = computed(() => lat.value != null && lng.value != null)

const googleMapsUrlFromApi = computed(() => {
  const u = props.restaurant?.google_maps_url
  return u && typeof u === 'string' && u.trim() ? u.trim() : null
})

/** Marker popup CTA: saved Google Maps URL from API when present; else OpenStreetMap at same coords. */
const popupMapLink = computed(() => {
  if (!hasMapCoordinates.value) return null
  if (googleMapsUrlFromApi.value) return googleMapsUrlFromApi.value
  return openStreetMapUrl(lat.value, lng.value, 15)
})

/** Only when URL exists but we could not get coordinates — open what the owner saved */
const savedMapUrlFallback = computed(() => {
  if (hasMapCoordinates.value) return null
  return googleMapsUrlFromApi.value
})

const hasMapOrLink = computed(() => hasMapCoordinates.value || !!savedMapUrlFallback.value)

function buildPopupContent() {
  const name = props.restaurant?.name ?? ''
  const addressRaw = props.restaurant?.address
  const address = addressRaw && typeof addressRaw === 'string' && addressRaw.trim() ? addressRaw.trim() : ''
  const link = popupMapLink.value
  if (!link) return ''

  const title = escapeHtml(name)
  const addressEscaped = address ? escapeHtml(address) : ''
  const ctaText = escapeHtml(t('public.openInMaps'))

  const addressHtml = addressEscaped
    ? `<p class="rms-public-map__popup-address">${addressEscaped}</p>`
    : ''

  return `<div class="rms-public-map__popup-content">
    <p class="rms-public-map__popup-title" title="${title}">${title || ' '}</p>
    ${addressHtml}
    <a href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer" class="rms-public-map__popup-cta"><span class="material-symbols-outlined rms-public-map__popup-cta-icon" aria-hidden="true">map</span>${ctaText}</a>
  </div>`
}

function initMap() {
  if (!mapEl.value || !hasMapCoordinates.value) return
  map = L.map(mapEl.value, {
    center: [lat.value, lng.value],
    zoom: 15,
    zoomControl: true,
    scrollWheelZoom: true,
  })
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map)

  const popupContent = buildPopupContent()
  const popupOptions = {
    maxWidth: 300,
    className: 'rms-public-map__popup',
    minWidth: 200,
  }
  marker = L.marker([lat.value, lng.value], { icon: DefaultIcon }).addTo(map)
  if (popupContent) {
    const popup = L.popup(popupOptions).setContent(popupContent)
    marker.bindPopup(popup)
    popup.on('add', () => {
      const link = popup.getElement()?.querySelector('.rms-public-map__popup-cta')
      if (link) {
        requestAnimationFrame(() => link.focus())
      }
    })
  }
  map.getPanes().mapPane.style.pointerEvents = 'auto'

  detachMapResizeListeners = attachMapResizeListeners(map)
  map.whenReady(() => {
    scheduleMapSizeSync()
  })
  nextTick(() => scheduleMapSizeSync())
}

function destroyMap() {
  detachMapResizeListeners()
  detachMapResizeListeners = () => {}
  if (marker) {
    marker.remove()
    marker = null
  }
  if (map) {
    map.remove()
    map = null
  }
}

onMounted(() => {
  if (hasMapCoordinates.value && mapEl.value) initMap()
})

onUnmounted(() => {
  destroyMap()
})

watch([lat, lng], () => {
  destroyMap()
  if (hasMapCoordinates.value && mapEl.value) {
    setTimeout(() => {
      initMap()
      nextTick(scheduleMapSizeSync)
    }, 50)
  }
})
</script>

<style scoped>
/* Contain stacking so Leaflet's high z-index (400–1000) does not cover the header */
.rms-public-map--stack-below-header {
  position: relative;
  z-index: 0;
}
/* iOS Safari: stable layer + explicit min height so Leaflet gets non-zero size */
.rms-public-map__container {
  min-height: inherit;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}
.rms-public-map__container :deep(.leaflet-container) {
  width: 100%;
  height: 100%;
  min-height: 280px;
}
.rms-public-map__container :deep(.leaflet-control-zoom) {
  margin: 0;
}
.rms-public-map__container :deep(.leaflet-control-attribution) {
  font-size: 10px;
}

/* Popup: scoped via class on Leaflet popup */
.rms-public-map__container :deep(.rms-public-map__popup .leaflet-popup-content-wrapper) {
  padding: 0;
  border-radius: 0.5rem;
}
.rms-public-map__container :deep(.rms-public-map__popup .leaflet-popup-content) {
  margin: 0;
  min-width: 0;
}
.rms-public-map__container :deep(.rms-public-map__popup-content) {
  padding: 14px 16px;
}
.rms-public-map__container :deep(.rms-public-map__popup-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.25;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rms-public-map__container :deep(.rms-public-map__popup-address) {
  font-size: 0.875rem;
  color: var(--rms-muted, #6b7280);
  margin: 0.25rem 0 0;
  line-height: 1.35;
}
/* Neutral outline button: no primary/brand in popup */
.rms-public-map__container :deep(.rms-public-map__popup-cta) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  margin-top: 12px;
  padding: 0.5rem 0.75rem;
  width: auto;
  box-sizing: border-box;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: color 0.15s, background-color 0.15s, border-color 0.15s;
}
.rms-public-map__container :deep(.rms-public-map__popup-cta:hover) {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}
.rms-public-map__container :deep(.rms-public-map__popup-cta:focus) {
  outline: none;
  box-shadow: 0 0 0 2px #9ca3af;
}
.rms-public-map__container :deep(.rms-public-map__popup-cta-icon) {
  font-size: 1rem;
}
@media (prefers-reduced-motion: reduce) {
  .rms-public-map__container :deep(.rms-public-map__popup .leaflet-popup-content-wrapper),
  .rms-public-map__container :deep(.rms-public-map__popup .leaflet-popup-tip) {
    transition: none;
  }
}
</style>
